// src/components/layout/Header.jsx
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  ChevronDown,
  TrendingUp,
  BarChart3,
  PieChart,
  Info,
  User,
  LogOut
} from 'lucide-react';
// Note: Make sure this logo path is correct for your project
// If logo import fails, replace with a fallback or remove the img tag
import logo from '../assets/images/logo.png';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  // Color theme
  const colors = {
    primary: '#22332D',
    secondary: '#6774AE',
    accent1: '#B7B6AF',
    accent2: '#6AA48A',
    accent3: '#4F886D',
    hover: '#70593E', // Light brown
  };

  // Navigation items - FIXED: removed semicolon and properly closed the array
  const navItems = [
    { 
      name: 'Projects', 
      icon: <TrendingUp className="w-4 h-4" />,
      dropdown: [
        { name: 'Agriculture', path: '/Projects/agriculture' },
        { name: 'Forestry', path: '/Projects/forestry' },
        { name: 'Industrial Process', path: '/Projects/industrial-process' },
        { name: 'Plastic Alternatives', path: '/Projects/plastic-alternatives' },
        { name: 'Renewable Energy', path: '/Projects/renewable-energy' },
        { name: 'Waste Management', path: '/Projects/waste-management' },
      ]
    },
    { 
      name: 'Invest ', 
      icon: <BarChart3 className="w-4 h-4" />, 
      dropdown: [
        { name: 'SCCredits', path: '/invest/sccredits' },
        { name: 'Transactions', path: '/invest/transactions' },
        { name: 'Retirements', path: '/invest/retirements' },
      ]
    },
    { 
      name: 'Analysis', 
      path: '/analysis', 
      icon: <PieChart className="w-4 h-4" />, 
      dropdown: [
        { name: 'Technical', path: '/analysis/technical' },
        { name: 'News', path: '/analysis/news' },
        { name: 'Reports', path: '/analysis/reports' },
      ]
    },
    {
      name: 'About Us',
      path: '/about',
      icon: <Info className="w-4 h-4" />,
      dropdown: []
    }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside (optional enhancement)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user dropdown when clicking outside
      if (activeDropdown === 'user' && !event.target.closest('.user-dropdown')) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeDropdown]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    // Add actual logout logic here (clear tokens, redirect, etc.)
    // For now, just close mobile menu if open
    setIsOpen(false);
    setActiveDropdown(null);
  };

  // Helper function to check if a link is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'shadow-lg py-2' : 'py-4'
      }`}
      style={{ 
        backgroundColor: scrolled ? colors.primary : 'rgba(34, 50, 45, 0.95)',
        backdropFilter: 'blur(8px)'
      }}
    >
      {/* Gradient top border - Same as footer */}
      <div 
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ 
          background: `linear-gradient(90deg, transparent, ${colors.hover}, ${colors.accent2}, ${colors.hover}, transparent)`,
        }}
      />

      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="z-50">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2"
            >
              <img 
                src={logo} 
                alt="SCMart Logo" 
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  // Optionally add a fallback text
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <span className="text-xl font-bold" style={{ color: colors.accent1 }}>
                <span style={{ color: colors.accent2 }}>SCMart</span>
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <div
                key={item.name}
                className="relative"
                onMouseEnter={() => setActiveDropdown(item.name)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={item.path}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
                  style={{
                    color: isActive(item.path) ? colors.hover : colors.accent1,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.hover;
                    e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive(item.path)) {
                      e.currentTarget.style.color = colors.accent1;
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <span style={{ color: colors.hover }}>{item.icon}</span>
                  {item.name}
                  {item.dropdown.length > 0 && (
                    <ChevronDown 
                      className={`w-4 h-4 transition-transform duration-300 ${
                        activeDropdown === item.name ? 'rotate-180' : ''
                      }`}
                      style={{ color: colors.hover }}
                    />
                  )}
                </Link>
                
                {item.dropdown.length > 0 && (
                  <AnimatePresence>
                    {activeDropdown === item.name && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 w-48 rounded-lg shadow-xl overflow-hidden"
                        style={{ 
                          backgroundColor: colors.primary,
                          border: `1px solid ${colors.hover}30`
                        }}
                      >
                        {item.dropdown.map((dropdownItem) => (
                          <Link
                            key={dropdownItem.name}
                            to={dropdownItem.path}
                            className="block px-4 py-2.5 text-sm transition-all duration-200"
                            style={{ color: colors.accent1 }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.hover}15`;
                              e.currentTarget.style.color = colors.hover;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = colors.accent1;
                            }}
                          >
                            {dropdownItem.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                )}
              </div>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {isAuthenticated ? (
              <div className="relative user-dropdown">
                <button
                  onClick={() => setActiveDropdown(activeDropdown === 'user' ? null : 'user')}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300"
                  style={{ backgroundColor: `${colors.hover}10` }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.hover}20`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                  }}
                >
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: colors.hover }}
                  >
                    <User className="w-4 h-4" style={{ color: colors.primary }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: colors.accent1 }}>John D.</span>
                  <ChevronDown 
                    className={`w-4 h-4 transition-transform duration-300 ${
                      activeDropdown === 'user' ? 'rotate-180' : ''
                    }`}
                    style={{ color: colors.hover }}
                  />
                </button>

                <AnimatePresence>
                  {activeDropdown === 'user' && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl overflow-hidden"
                      style={{ 
                        backgroundColor: colors.primary,
                        border: `1px solid ${colors.hover}30`
                      }}
                    >
                      <Link
                        to="/profile"
                        className="block px-4 py-2.5 text-sm transition-all duration-200"
                        style={{ color: colors.accent1 }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.hover}15`;
                          e.currentTarget.style.color = colors.hover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = colors.accent1;
                        }}
                        onClick={() => setActiveDropdown(null)}
                      >
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="block px-4 py-2.5 text-sm transition-all duration-200"
                        style={{ color: colors.accent1 }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.hover}15`;
                          e.currentTarget.style.color = colors.hover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = colors.accent1;
                        }}
                        onClick={() => setActiveDropdown(null)}
                      >
                        Settings
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-all duration-200"
                        style={{ color: colors.accent1 }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.hover}15`;
                          e.currentTarget.style.color = colors.hover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = colors.accent1;
                        }}
                      >
                        <LogOut className="w-4 h-4" style={{ color: colors.hover }} />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <Link
                  to="/signin"
                  className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300"
                  style={{ color: colors.accent1 }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.hover;
                    e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = colors.accent1;
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Sign In
                </Link>
                <Link
                  to="/signup"
                  className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all duration-300"
                  style={{ 
                    backgroundColor: colors.hover,
                    color: colors.primary
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = colors.accent2;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.hover;
                  }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden z-50 p-2 rounded-lg transition-all duration-300"
            style={{ backgroundColor: `${colors.hover}10` }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${colors.hover}20`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = `${colors.hover}10`;
            }}
            aria-label={isOpen ? "Close menu" : "Open menu"}
          >
            {isOpen ? (
              <X className="w-6 h-6" style={{ color: colors.accent1 }} />
            ) : (
              <Menu className="w-6 h-6" style={{ color: colors.accent1 }} />
            )}
          </button>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
                className="absolute top-full left-0 right-0 lg:hidden max-h-[calc(100vh-64px)] overflow-y-auto"
                style={{ 
                  backgroundColor: colors.primary,
                  borderTop: `1px solid ${colors.hover}30`
                }}
              >
                <div className="px-4 py-4 space-y-3">
                  {/* Mobile User Info */}
                  {isAuthenticated && (
                    <div 
                      className="flex items-center gap-3 p-3 rounded-lg"
                      style={{ backgroundColor: `${colors.hover}10` }}
                    >
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: colors.hover }}
                      >
                        <User className="w-5 h-5" style={{ color: colors.primary }} />
                      </div>
                      <div>
                        <p className="text-sm font-medium" style={{ color: colors.accent1 }}>John Doe</p>
                        <p className="text-xs" style={{ color: colors.hover }}>john@example.com</p>
                      </div>
                    </div>
                  )}

                  {/* Mobile Navigation */}
                  <nav className="space-y-1">
                    {navItems.map((item) => (
                      <div key={item.name}>
                        {item.dropdown.length > 0 ? (
                          <>
                            <button
                              onClick={() => setActiveDropdown(
                                activeDropdown === item.name ? null : item.name
                              )}
                              className="flex items-center justify-between w-full px-4 py-3 rounded-lg transition-all duration-300"
                              style={{ color: colors.accent1 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                                e.currentTarget.style.color = colors.hover;
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                                e.currentTarget.style.color = colors.accent1;
                              }}
                            >
                              <span className="flex items-center gap-2">
                                <span style={{ color: colors.hover }}>{item.icon}</span>
                                {item.name}
                              </span>
                              <ChevronDown 
                                className={`w-4 h-4 transition-transform duration-300 ${
                                  activeDropdown === item.name ? 'rotate-180' : ''
                                }`}
                                style={{ color: colors.hover }}
                              />
                            </button>
                            
                            <AnimatePresence>
                              {activeDropdown === item.name && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="ml-6 pl-4 space-y-1 overflow-hidden"
                                  style={{ borderLeft: `1px solid ${colors.hover}30` }}
                                >
                                  {item.dropdown.map((dropdownItem) => (
                                    <Link
                                      key={dropdownItem.name}
                                      to={dropdownItem.path}
                                      className="block px-4 py-2 text-sm rounded-lg transition-all duration-300"
                                      style={{ color: colors.accent1 }}
                                      onClick={() => setIsOpen(false)}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                                        e.currentTarget.style.color = colors.hover;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = colors.accent1;
                                      }}
                                    >
                                      {dropdownItem.name}
                                    </Link>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </>
                        ) : (
                          <Link
                            to={item.path}
                            className="flex items-center gap-2 px-4 py-3 rounded-lg transition-all duration-300"
                            style={{ color: colors.accent1 }}
                            onClick={() => setIsOpen(false)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                              e.currentTarget.style.color = colors.hover;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.color = colors.accent1;
                            }}
                          >
                            <span style={{ color: colors.hover }}>{item.icon}</span>
                            {item.name}
                          </Link>
                        )}
                      </div>
                    ))}
                  </nav>

                  {/* Mobile Auth */}
                  <div className="pt-3 space-y-2">
                    {isAuthenticated ? (
                      <button
                        onClick={handleLogout}
                        className="flex items-center justify-center gap-2 w-full px-4 py-3 text-center rounded-lg transition-all duration-300"
                        style={{ 
                          border: `1px solid ${colors.hover}`,
                          color: colors.accent1
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                          e.currentTarget.style.color = colors.hover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = colors.accent1;
                        }}
                      >
                        <LogOut className="w-4 h-4" style={{ color: colors.hover }} />
                        Logout
                      </button>
                    ) : (
                      <>
                        <Link
                          to="/signin"
                          className="block w-full px-4 py-3 text-center rounded-lg transition-all duration-300"
                          style={{ 
                            border: `1px solid ${colors.hover}`,
                            color: colors.accent1
                          }}
                          onClick={() => setIsOpen(false)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = `${colors.hover}10`;
                            e.currentTarget.style.color = colors.hover;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = colors.accent1;
                          }}
                        >
                          Sign In
                        </Link>
                        <Link
                          to="/signup"
                          className="block w-full px-4 py-3 text-center rounded-lg transition-all duration-300"
                          style={{ 
                            backgroundColor: colors.hover,
                            color: colors.primary
                          }}
                          onClick={() => setIsOpen(false)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colors.accent2;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = colors.hover;
                          }}
                        >
                          Get Started
                        </Link>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;